import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  MEMBER_STATUS_LABELS,
  formatCents,
  formatCentsPlain,
  type Paginated,
  type SalesPanelResponse,
  type User,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { PeriodBar } from '@/components/PeriodBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { monthRange } from '@/lib/queries';
import { downloadCsv } from '@/lib/csv';
import { MEMBER_STATUS_TONE } from './Associados';

type PanelRow = SalesPanelResponse['rows'][number];

/**
 * Painel de vendas do clube — quanto cada vendedor fechou no período.
 *
 * "Recebido" é o que de fato entrou (recebimentos da venda); a diferença para
 * o total está em aberto no contas a receber.
 */
export function PainelVendasPage() {
  const { can } = useAuth();
  const range = monthRange();

  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [sellerId, setSellerId] = useState('');

  // O filtro por vendedor só aparece para quem pode listar usuários — o
  // vendedor comum vê o painel completo do período, sem recorte.
  const canPickSeller = can('usuarios:ver');
  const sellersQuery = useQuery({
    queryKey: ['users', 'sellers'],
    queryFn: () => api.get<Paginated<User>>('/users', { perPage: 100 }),
    enabled: canPickSeller,
    staleTime: 5 * 60_000,
  });

  const query = useQuery({
    queryKey: ['clube-sales-panel', { from, to, sellerId }],
    queryFn: () =>
      api.get<SalesPanelResponse>('/clube/painel-vendas', {
        from,
        to,
        sellerId: sellerId || undefined,
      }),
  });

  const data = query.data;

  const columns: Array<Column<PanelRow>> = [
    { key: 'soldAt', label: 'Data', mono: true, render: (row) => formatDate(row.soldAt) },
    { key: 'sellerName', label: 'Vendedor', render: (row) => blank(row.sellerName) },
    { key: 'memberName', label: 'Associado', strong: true, render: (row) => row.memberName },
    { key: 'planName', label: 'Plano', render: (row) => row.planName },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <StatusBadge kind={MEMBER_STATUS_TONE[row.status]}>{MEMBER_STATUS_LABELS[row.status]}</StatusBadge>
      ),
    },
    {
      key: 'totalCents',
      label: 'Valor da venda',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.totalCents),
    },
    {
      key: 'receivedCents',
      label: 'Recebido',
      right: true,
      mono: true,
      render: (row) => (
        <span style={row.receivedCents < row.totalCents ? { color: T.amber } : { color: T.green }}>
          {formatCents(row.receivedCents)}
        </span>
      ),
    },
  ];

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(`painel-vendas-${from}-a-${to}`, [
      ['Data', 'Vendedor', 'Associado', 'Plano', 'Situação', 'Valor da venda', 'Recebido'],
      ...data.rows.map((row) => [
        formatDate(row.soldAt),
        row.sellerName ?? '',
        row.memberName,
        row.planName,
        MEMBER_STATUS_LABELS[row.status],
        formatCentsPlain(row.totalCents),
        formatCentsPlain(row.receivedCents),
      ]),
      ['TOTAL', '', '', '', String(data.totals.count), formatCentsPlain(data.totals.totalCents), formatCentsPlain(data.totals.receivedCents)],
    ]);
  };

  return (
    <>
      <WorkspaceHeader
        title="Painel de vendas"
        actions={
          can('associados:exportar') && data && data.rows.length > 0 ? (
            <Button icon={Download} onClick={exportCsv}>
              Exportar CSV
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          {canPickSeller && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <span className="muted">Vendedor</span>
              <select
                className="input"
                value={sellerId}
                onChange={(event) => setSellerId(event.target.value)}
                style={{ width: 200 }}
              >
                <option value="">Todos</option>
                {(sellersQuery.data?.data ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </PeriodBar>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Vendas no período</span>
                </div>
                <div className="metric__value">{data.totals.count}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Total vendido</span>
                </div>
                <div className="metric__value">{formatCents(data.totals.totalCents)}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Recebido</span>
                </div>
                <div className="metric__value">{formatCents(data.totals.receivedCents)}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Em aberto</span>
                </div>
                <div
                  className={`metric__value${
                    data.totals.totalCents - data.totals.receivedCents > 0 ? ' metric__value--bad' : ''
                  }`}
                >
                  {formatCents(data.totals.totalCents - data.totals.receivedCents)}
                </div>
              </div>
            </div>

            {data.rows.length === 0 ? (
              <EmptyState
                title="Nenhuma venda no período"
                description="Ajuste o intervalo ou o vendedor para ver os resultados."
              />
            ) : (
              <SmartDataTable caption="Vendas do clube no período" columns={columns} rows={data.rows.map((row) => ({ ...row, id: row.memberId }))} />
            )}
          </>
        )}
      </div>
    </>
  );
}
